import { IStation, ocStation } from '@helpers/types';
import { useStationRequest } from '@hooks/use-station-request.ts';
import { useCallback, useEffect, useState } from 'react';

const useStationSelector = (onChange: ocStation, requestedCountry?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [stations, setStations] = useState<Array<IStation>>([]);
  const { getStations: getApolloStations } = useStationRequest();

  const onChangeStation = useCallback(
    (_: any, value: IStation | null) => {
      if (value) onChange(value);
    },
    [onChange],
  );

  const fetchStations = useCallback(async () => {
    setIsLoading(true);
    if (!requestedCountry) return [];

    // Using the Apollo query to get stations list
    const stationNames = await getApolloStations(requestedCountry);

    // Transform simple strings to IStation objects
    // Note: The original code expected detailed objects from getStationDetails (REST),
    // but the GQL query returns an array of strings.
    // We map them to basic IStation objects.
    const flatStations: IStation[] = stationNames.map((name: string) => ({
      station: name,
      country: requestedCountry,
      // Coordinates are not returned by the simple list query,
      // they would require individual queries or a different endpoint if needed for selection context
      coordinates: [0, 0] as [number, number],
    }));

    if (requestedCountry === 'MEX') {
      return [...flatStations];
    }

    return flatStations;
  }, [requestedCountry, getApolloStations]);

  useEffect(() => {
    setIsLoading(true);
    fetchStations().then((stations) => {
      setStations(stations);
      setIsLoading(false);
    });
  }, [fetchStations]);

  return {
    stations,
    onChangeStation,
    isLoading,
  };
};

export default useStationSelector;
